import { useEffect } from 'react';

export const useSEO = ({
  title,
  description,
  keywords = '',
  canonicalUrl = '',
  ogImage = '',
  ogType = 'website'
}) => {
  useEffect(() => {
    // Update document title
    if (title) {
      document.title = title;
    }

    // Helper function to update or create meta tag
    const updateMetaTag = (selector, attribute, content) => {
      if (!content) return;
      
      let element = document.querySelector(selector);
      if (!element) {
        element = document.createElement('meta');
        const [attr, value] = attribute.split('=');
        element.setAttribute(attr, value.replace(/['"]/g, ''));
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Helper function to update link tag
    const updateLinkTag = (rel, href) => {
      if (!href) return;
      
      let element = document.querySelector(`link[rel="${rel}"]`);
      if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        document.head.appendChild(element);
      }
      element.setAttribute('href', href);
    };

    // Update meta description
    updateMetaTag('meta[name="description"]', 'name=description', description);

    // Update meta keywords
    if (keywords) {
      updateMetaTag('meta[name="keywords"]', 'name=keywords', keywords);
    }

    // Update canonical URL
    const fullCanonicalUrl = canonicalUrl || window.location.href;
    updateLinkTag('canonical', fullCanonicalUrl);

    // Update Open Graph tags
    updateMetaTag('meta[property="og:title"]', 'property=og:title', title);
    updateMetaTag('meta[property="og:description"]', 'property=og:description', description);
    updateMetaTag('meta[property="og:url"]', 'property=og:url', fullCanonicalUrl);
    updateMetaTag('meta[property="og:type"]', 'property=og:type', ogType);
    
    if (ogImage) {
      updateMetaTag('meta[property="og:image"]', 'property=og:image', ogImage);
    }

    // Update Twitter Card tags
    updateMetaTag('meta[name="twitter:card"]', 'name=twitter:card', 'summary_large_image');
    updateMetaTag('meta[name="twitter:title"]', 'name=twitter:title', title);
    updateMetaTag('meta[name="twitter:description"]', 'name=twitter:description', description);
    
    if (ogImage) {
      updateMetaTag('meta[name="twitter:image"]', 'name=twitter:image', ogImage);
    }

    // Cleanup function (optional - keeps last set values)
    return () => {
      // Meta tags persist for SEO purposes
    };
  }, [title, description, keywords, canonicalUrl, ogImage, ogType]);
};

// Hook for JSON-LD structured data
export const useStructuredData = (schemaData) => {
  useEffect(() => {
    if (!schemaData) return;

    // Remove all existing JSON-LD script tags to avoid duplicates
    const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
    existingScripts.forEach(script => script.remove());

    // Handle both single schema and array of schemas
    const schemas = Array.isArray(schemaData) ? schemaData : [schemaData];
    
    // Create script tag for each schema
    schemas.forEach(schema => {
      const scriptTag = document.createElement('script');
      scriptTag.type = 'application/ld+json';
      scriptTag.textContent = JSON.stringify(schema);
      document.head.appendChild(scriptTag);
    });

    return () => {
      // Cleanup on unmount
      const scripts = document.querySelectorAll('script[type="application/ld+json"]');
      scripts.forEach(script => script.remove());
    };
  }, [schemaData]);
};
